// Super admin → SMS Templates
// Wording of the text messages sent to bidders. Only the super admin can edit these.
// Backend: GET /sms-template, POST /sms-template/update, POST /sms-template/reset

import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { get, post } from "../../utils/apiManager";

// Characters a standard text can hold without switching to Unicode (70 per text)
const GSM_BASIC = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = "^{}\\[~]|€"; // count as 2 characters

const smsSize = (text) => {
  const chars = Array.from(text || "");
  const unicode = chars.some((ch) => !GSM_BASIC.includes(ch) && !GSM_EXTENDED.includes(ch));
  if (unicode) {
    const len = chars.length;
    return { length: len, texts: len <= 70 ? 1 : Math.ceil(len / 67), unicode: true };
  }
  const len = chars.reduce((n, ch) => n + (GSM_EXTENDED.includes(ch) ? 2 : 1), 0);
  return { length: len, texts: len <= 160 ? 1 : Math.ceil(len / 153), unicode: false };
};

// Same fill-in rules the server uses
const fillSample = (body, placeholders) => {
  const samples = Object.fromEntries((placeholders || []).map((p) => [p.name, p.sample]));
  return String(body || "")
    .replace(/\{([a-zA-Z0-9_]+)\}/g, (m, name) => (samples[name] !== undefined ? samples[name] : ""))
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([.,!?])/g, "$1")
    .trim();
};

const styles = {
  card: { background: "#fff", borderRadius: 12, border: "1px solid #e8ecf4", boxShadow: "0 1px 3px rgba(15,23,42,.04)", marginBottom: 16 },
  badge: (bg, color) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 100, fontSize: 12, fontWeight: 500, background: bg, color, marginLeft: 8, verticalAlign: "middle" }),
  chip: { border: "1px solid #dbe3f0", background: "#f8fafc", color: "#334155", borderRadius: 6, padding: "3px 8px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" },
  button: (primary, disabled) => ({
    border: primary ? "none" : "1px solid #d5dbe7",
    background: primary ? (disabled ? "#cbd5e1" : "#AD2429") : "#fff",
    color: primary ? "#fff" : "#334155",
    borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 600,
    cursor: disabled ? "default" : "pointer", fontFamily: "inherit",
  }),
};

const Toggle = ({ checked, onChange, disabled }) => (
  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer", fontSize: 13, color: "#475569", userSelect: "none" }}>
    <span
      onClick={() => !disabled && onChange(!checked)}
      style={{ width: 36, height: 20, borderRadius: 20, background: checked ? "#15803d" : "#cbd5e1", position: "relative", transition: "background .15s", display: "inline-block" }}
    >
      <span style={{ position: "absolute", top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: 16, background: "#fff", transition: "left .15s", boxShadow: "0 1px 2px rgba(0,0,0,.2)" }} />
    </span>
    {checked ? "On" : "Off"}
  </label>
);

const TemplateCard = ({ template, maxLength, onSaved }) => {
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);
  const textRef = useRef(null);

  useEffect(() => { setBody(template.body); }, [template.body]);

  const dirty = body.trim() !== template.body.trim();
  const size = smsSize(fillSample(body, template.placeholders));
  const tooLong = body.trim().length > maxLength;

  const insert = (name) => {
    const el = textRef.current;
    const token = `{${name}}`;
    if (!el) { setBody((b) => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  const save = async (changes) => {
    setSaving(true);
    const res = await post("/sms-template/update", { key: template.key, ...changes });
    setSaving(false);
    if (res?.status) { toast.success(res.message || "Saved"); onSaved(); }
    else toast.error(res?.message || "Could not save");
  };

  const reset = async () => {
    const answer = await Swal.fire({
      title: "Reset to default?",
      text: "This puts back the original wording for this text.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Reset",
      confirmButtonColor: "#AD2429",
    });
    if (!answer.isConfirmed) return;
    setSaving(true);
    const res = await post("/sms-template/reset", { key: template.key });
    setSaving(false);
    if (res?.status) { toast.success(res.message || "Reset"); onSaved(); }
    else toast.error(res?.message || "Could not reset");
  };

  return (
    <div style={{ ...styles.card, opacity: template.isActive ? 1 : 0.75 }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #eef1f6", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>
            {template.title}
            {template.sentNow
              ? <span style={styles.badge("#dcfce7", "#15803d")}>Sent now</span>
              : <span style={styles.badge("#f1f5f9", "#64748b")}>Not sent yet</span>}
            {template.isCustomized ? <span style={styles.badge("#eff6ff", "#1d4ed8")}>Edited</span> : null}
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{template.description}</div>
        </div>
        <Toggle checked={template.isActive} disabled={saving} onChange={(v) => save({ isActive: v })} />
      </div>

      <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
        <div>
          <textarea
            ref={textRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d5dbe7", borderRadius: 8, padding: "10px 12px", fontSize: 14, lineHeight: 1.45, fontFamily: "inherit", color: "#0f172a", resize: "vertical", outline: "none" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
            {template.placeholders.map((p) => (
              <button key={p.name} type="button" style={styles.chip} title={p.label} onClick={() => insert(p.name)}>
                {`{${p.name}}`}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: tooLong ? "#b91c1c" : "#94a3b8", marginTop: 8 }}>
            About {size.length} characters with sample values · {size.texts} {size.texts === 1 ? "text" : "texts"} per message
            {size.unicode ? " · contains special characters (curly quotes, emoji), so each text holds only 70" : ""}
            {tooLong ? ` · over the ${maxLength} character limit` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button type="button" style={styles.button(true, !dirty || saving || tooLong)} disabled={!dirty || saving || tooLong} onClick={() => save({ body })}>
              {saving ? "Saving…" : "Save"}
            </button>
            {dirty ? <button type="button" style={styles.button(false)} onClick={() => setBody(template.body)}>Undo changes</button> : null}
            {template.isCustomized && !dirty ? <button type="button" style={styles.button(false, saving)} disabled={saving} onClick={reset}>Reset to default</button> : null}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>Preview</div>
          <div style={{ background: "#f1f5f9", borderRadius: 12, padding: 14 }}>
            <div style={{ background: "#e2e8f0", color: "#0f172a", borderRadius: "14px 14px 14px 4px", padding: "10px 12px", fontSize: 14, lineHeight: 1.45, maxWidth: 320, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {fillSample(body, template.placeholders) || " "}
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
            {template.placeholders.map((p) => `{${p.name}} = ${p.label.toLowerCase()}`).join(" · ")}
          </div>
        </div>
      </div>
    </div>
  );
};

const SmsTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [maxLength, setMaxLength] = useState(480);
  const [loading, setLoading] = useState(true);

  const load = () => {
    get("/sms-template").then((res) => {
      setLoading(false);
      if (res?.status) {
        setTemplates(res.datas || []);
        if (res.maxLength) setMaxLength(res.maxLength);
      } else {
        toast.error(res?.message || "Could not load SMS templates");
      }
    }).catch(() => { setLoading(false); toast.error("Could not load SMS templates"); });
  };

  useEffect(() => { load(); }, []);

  const sentNow = templates.filter((t) => t.sentNow);
  const later = templates.filter((t) => !t.sentNow);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: "#0f172a", margin: 0 }}>SMS Templates</h2>
        <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 0", maxWidth: 820 }}>
          Text messages sent to bidders. A text only goes out when the company has SMS turned on and the bidder has a
          verified phone and hasn't opted out of that kind of text. Turning a template off stops that text for every company.
          Login and phone verification codes come from Twilio and are not edited here.
        </p>
      </div>

      {loading ? <div style={{ color: "#94a3b8", fontSize: 14 }}>Loading…</div> : null}

      {sentNow.length ? <h3 style={{ fontSize: 14, fontWeight: 600, color: "#334155", margin: "8px 0 12px" }}>Sent now</h3> : null}
      {sentNow.map((t) => <TemplateCard key={t.key} template={t} maxLength={maxLength} onSaved={load} />)}

      {later.length ? (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: "#334155", margin: "24px 0 4px" }}>Ready for later</h3>
          <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>Nothing sends these yet. The wording will be used once they are turned on in the code.</p>
        </>
      ) : null}
      {later.map((t) => <TemplateCard key={t.key} template={t} maxLength={maxLength} onSaved={load} />)}
    </div>
  );
};

export default SmsTemplates;
